// GastronomyGallery — image carousel for the homepage "Gastronomy"
// section. Mirrors the thumbnail-strip pattern used by
// PropertyCardGallery (large main image + dark horizontally scrollable
// thumbnail strip with gold-outline active state). Includes the brand
// X monogram anchored to the bottom-right corner so the section reads
// consistently with the rest of the editorial image surfaces.

import React, { useState } from "react";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import OptimizedImage from "@/components/OptimizedImage";

// Curated Moroccan dish photos — tagine, couscous, mint tea ritual,
// pastilla, Berber bread, harira. Replace freely via CMS later.
const DEFAULT_DISHES = [
  {
    src: "https://images.pexels.com/photos/6260921/pexels-photo-6260921.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Moroccan lamb tagine with vegetables",
  },
  {
    src: "https://images.pexels.com/photos/8696547/pexels-photo-8696547.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Couscous royal with seven vegetables",
  },
  {
    src: "https://images.pexels.com/photos/6260828/pexels-photo-6260828.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Mint tea poured from a silver teapot",
  },
  {
    src: "https://images.pexels.com/photos/12737912/pexels-photo-12737912.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Moroccan pastilla, almonds and cinnamon",
  },
  {
    src: "https://images.pexels.com/photos/5848468/pexels-photo-5848468.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Khobz — traditional Berber bread",
  },
  {
    src: "https://images.pexels.com/photos/8696551/pexels-photo-8696551.jpeg?auto=compress&cs=tinysrgb&w=1600",
    alt: "Harira soup with dates",
  },
];

const GastronomyGallery = ({ initialSrc, testIdPrefix = "gastronomy-gallery", scope = "home.gastronomy" }) => {
  // Use the brand-default GASTRONOMY_IMAGE as the lead frame when
  // provided so this carousel can drop into the existing section
  // without losing the curated cover photo.
  const images = React.useMemo(() => {
    const pool = [];
    const seen = new Set();
    const push = (img) => {
      if (!img || !img.src || seen.has(img.src)) return;
      seen.add(img.src);
      pool.push(img);
    };
    if (initialSrc) push({ src: initialSrc, alt: "Moroccan tagine" });
    DEFAULT_DISHES.forEach(push);
    return pool;
  }, [initialSrc]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [brokenUrls, setBrokenUrls] = useState(() => new Set());

  const markBroken = (url) =>
    setBrokenUrls((cur) => {
      if (cur.has(url)) return cur;
      const next = new Set(cur);
      next.add(url);
      return next;
    });

  const visible = images.filter((img) => !brokenUrls.has(img.src));
  const safeIdx = Math.min(Math.max(activeIdx, 0), Math.max(visible.length - 1, 0));

  if (visible.length === 0) return null;

  return (
    <div className="block" data-testid={testIdPrefix} data-cms-scope={scope || undefined}>
      {/* Main image area — cross-fades between frames */}
      <div
        className="relative block overflow-hidden h-[60vh] md:h-[70vh]"
        data-testid={`${testIdPrefix}-main`}
      >
        {visible.map((img, idx) => (
          <OptimizedImage
            key={img.src}
            src={img.src}
            alt={img.alt}
            priority={idx === 0}
            sizes="(min-width: 768px) 50vw, 100vw"
            onError={() => markBroken(img.src)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out ${
              idx === safeIdx ? "opacity-100" : "opacity-0"
            }`}
            objectFit="cover"
            data-cms-role={`dish.${idx}`}
          />
        ))}
        <BrandMonogramEdge
          size="lg"
          opacity={0.5}
          testIdPrefix={`${testIdPrefix}-brand`}
        />
      </div>

      {/* Thumbnail strip — same dark band + gold-outline active state
          used on PropertyCardGallery. */}
      {visible.length > 1 ? (
        <div
          className="bg-[#0F0B09] px-3 py-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid={`${testIdPrefix}-thumbs`}
        >
          <div className="flex items-center gap-2 min-w-min">
            {visible.map((img, idx) => {
              const active = idx === safeIdx;
              return (
                <button
                  type="button"
                  key={`gthumb-${img.src}`}
                  onClick={() => setActiveIdx(idx)}
                  className={`relative shrink-0 w-16 sm:w-20 aspect-[4/3] overflow-hidden transition-all duration-300 ${
                    active
                      ? "outline outline-2 outline-offset-2 outline-[#D4A373] opacity-100"
                      : "opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Show image ${idx + 1}`}
                  aria-current={active}
                  data-testid={`${testIdPrefix}-thumb-${idx}`}
                >
                  <OptimizedImage
                    src={img.src}
                    alt=""
                    widths={[80, 160, 240]}
                    sizes="80px"
                    onError={() => markBroken(img.src)}
                    className="absolute inset-0 w-full h-full object-cover"
                    aria-hidden="true"
                    objectFit="cover"
                    data-cms-role={`dish.${idx}`}
                  />
                  {!active ? (
                    <span className="absolute inset-0 bg-[#1A1513]/30 pointer-events-none" aria-hidden="true" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GastronomyGallery;
